// src/autodesk_usage/hooks/useCorporateChildren.ts
// All comments in English only
import { useQuery } from "@tanstack/react-query";
import {
  getCorporateChildren,
  type ChildCompanyItem,
} from "../api/corporate";

export interface UseCorporateChildrenResult {
  data?: ChildCompanyItem[];
  isLoading: boolean;
  error: unknown;
}

function normalizeCorporateNames(value?: string | string[] | null) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return Array.from(
    new Set(list.map((item) => item.trim()).filter(Boolean))
  ).sort();
}

function normalizeChildPayload(payload: unknown): ChildCompanyItem[] {
  if (Array.isArray(payload)) {
    return payload as ChildCompanyItem[];
  }
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as any).items)
  ) {
    return (payload as any).items as ChildCompanyItem[];
  }
  return [];
}

export function useCorporateChildren(
  corporateName?: string | string[] | null
): UseCorporateChildrenResult {
  const corporateNames = normalizeCorporateNames(corporateName);
  const corporateKey = corporateNames.join("|");
  const query = useQuery<ChildCompanyItem[]>({
    queryKey: ["corporate-children", corporateKey],
    queryFn: async () => {
      if (!corporateNames.length) return [];
      const res = await getCorporateChildren(corporateNames);
      return normalizeChildPayload(res.data);
    },
    enabled: corporateNames.length > 0,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
